import { DateTime, DurationObjectUnits } from 'luxon';
import { UtcZone } from '@unistocks/shared/utils/date-utils';
import { TimePeriod } from '@unistocks/types';
import { TimeSeries } from '@unistocks/types/time-series';
import { groupDataItems } from './group';
import { getPeriodStart } from './period';

/**
 * Fills gaps in a time series by carrying forward the last available value (LOCF) at each
 * timestamp of the given timeline. Timestamps before the first available point are omitted.
 */
export const fillMissingPoints = (timeSeries: TimeSeries, timeline: number[]): TimeSeries => {
  if (!timeSeries.length || !timeline.length) return timeSeries;

  const valueByTimestamp = new Map(timeSeries.map(p => [p.timestamp, p.value]));
  const filled: TimeSeries = [];
  let lastValue: number | null = null;

  for (const timestamp of timeline) {
    const current = valueByTimestamp.get(timestamp) ?? null;
    if (current == null) {
      if (lastValue == null) continue;
      filled.push({ timestamp, value: lastValue });
    } else {
      lastValue = current;
      filled.push({ timestamp, value: current });
    }
  }

  return filled;
};

const PERIOD_UNITS: Record<TimePeriod, DurationObjectUnits> = {
  year: { years: 1 },
  quarter: { quarters: 1 },
  month: { months: 1 },
  week: { weeks: 1 },
  day: { days: 1 }
};

const createPeriodTimeline = (startDate: number, endDate: number, groupBy: TimePeriod): number[] => {
  const end = getPeriodStart(endDate, groupBy);
  const timeline: number[] = [];
  // Built directly from millis: createDate truncates every input to the local
  // start-of-day, which would shift the UTC period boundary.
  let cursor = DateTime.fromMillis(getPeriodStart(startDate, groupBy), { zone: UtcZone });

  while (cursor.toMillis() <= end) {
    timeline.push(cursor.toMillis());
    cursor = cursor.plus(PERIOD_UNITS[groupBy]);
  }

  return timeline;
};

/**
 * Normalizes comparison chart data to match a portfolio's value scale.
 * Maps each price so that `baseValue` equals `initialValue`.
 */
export const normalizeComparisonData = (
  items: TimeSeries,
  initialValue: number,
  baseValue: number
): TimeSeries => {
  if (!items.length || initialValue === 0 || baseValue === 0) return [];

  return items.map(item => ({
    timestamp: item.timestamp,
    value: (item.value / baseValue) * initialValue
  }));
};

/**
 * Slices, groups and normalizes comparison data so it starts at `initialValue`,
 * aligned to the same period boundaries as the main portfolio series. Missing periods
 * are carried forward (LOCF) from the last available value up to the end of the range,
 * so the comparison series has a value at every portfolio point.
 */
export const selectComparisonData = (
  entries: TimeSeries,
  startDate: number,
  endDate: number,
  initialValue: number,
  groupBy: TimePeriod
): TimeSeries => {
  const periodStartMillis = getPeriodStart(startDate, groupBy);
  const startIndex = entries.findIndex(x => x.timestamp >= periodStartMillis);
  const endIndex = entries.findIndex(x => x.timestamp >= endDate);

  const sliced = entries.slice(
    startIndex === -1 ? 0 : startIndex,
    endIndex === -1 ? undefined : endIndex + 1
  );

  if (!sliced.length) return [];

  // Base value is the value at the start of the date range (the first entry on or
  // after the exact range start). This keeps the comparison index starting on the
  // same point whether the data is grouped or not — grouping must not shift the
  // normalization base to the end of the first period (its close value).
  const baseEntry = sliced.find(x => x.timestamp >= startDate) ?? sliced[0];
  const baseValue = baseEntry.value;

  const grouped = groupDataItems(sliced, { groupBy, groupValue: 'close' });
  const normalized = normalizeComparisonData(grouped, initialValue, baseValue);

  return fillMissingPoints(normalized, createPeriodTimeline(startDate, endDate, groupBy));
};
